import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useMemo } from 'react';
import { getTestIds } from 'seagull/utils/TestIds';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { ChipConnector } from '~/components/AiSearchChips/ChipConnector/ChipConnector';
import { FilterChip } from '~/components/AiSearchChips/FilterChip/FilterChip';
import { useLocaleKeys } from '~/i18n';
import { CREDIT_CARD_FEES_PAID_BY } from '~/types/api/drafts-search';
import { ccFeesHasValues } from '../../settings-predicates';
import { useSettingsChipTone } from '../settingsChipTone';
import { creditCardFeesLabel, creditCardFeesSelection, removeAgreementField, removePaymentField, residualAgreementDescriptors, setRequirePaymentMethod, termsNameFromInput } from './SettingsChips.utils';
export const GetSettingsChipsTID = (parentTestId)=>getTestIds(parentTestId);
export const SettingsChips = (param)=>{
    let { testId, paymentSettings, agreementSettings, onPaymentSettingsChange, onAgreementSettingsChange } = param;
    const { SettingsChipsTestIds } = useMemo(()=>GetSettingsChipsTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('pages.draftsDashboard.aiSearch');
    const tone = useSettingsChipTone();
    const textTexts = {
        apply: t.applyButton()
    };
    const ccFeesItems = CREDIT_CARD_FEES_PAID_BY.map((value)=>({
            value,
            label: creditCardFeesLabel(value, t)
        }));
    const units = [];
    const residualChip = (descriptor)=>/*#__PURE__*/ _jsx(FilterChip, {
            testId: SettingsChipsTestIds.Chip(descriptor.field),
            fieldLabel: descriptor.fieldLabel,
            valueLabel: descriptor.valueLabel,
            onRemove: descriptor.onRemove,
            removeBiName: "drafts_ai_search_chip_remove_settings_".concat(descriptor.field),
            tone: tone
        });
    if (paymentSettings != null) {
        const settings = paymentSettings;
        if (settings.requirePaymentMethod != null) {
            const required = settings.requirePaymentMethod;
            units.push({
                field: 'requirePaymentMethod',
                node: /*#__PURE__*/ _jsx(AiFilterChip, {
                    mode: "toggle",
                    testId: SettingsChipsTestIds.Chip('requirePaymentMethod'),
                    fieldLabel: t.settingsPaymentMethodLabel(),
                    valueLabel: required ? t.settingsPaymentMethodRequired() : t.settingsPaymentMethodNotRequired(),
                    tone: tone,
                    onToggle: ()=>onPaymentSettingsChange(setRequirePaymentMethod(settings, !required)),
                    onRemove: ()=>onPaymentSettingsChange(removePaymentField(settings, 'requirePaymentMethod')),
                    biPrefix: "drafts_ai_search_chip_settings_require_payment_method"
                })
            });
        }
        const ccFees = settings.creditCardFeesPaidBy;
        if (ccFees != null && ccFeesHasValues(ccFees)) {
            var _ccFees_in;
            const selected = ccFees.nin != null ? ccFees.nin : (_ccFees_in = ccFees.in) !== null && _ccFees_in !== void 0 ? _ccFees_in : [];
            const operator = ccFees.nin != null ? 'exclude' : 'include';
            units.push({
                field: 'creditCardFeesPaidBy',
                node: /*#__PURE__*/ _jsx(AiFilterChip, {
                    mode: "checkboxes",
                    testId: SettingsChipsTestIds.Chip('creditCardFeesPaidBy'),
                    fieldLabel: t.settingsCcFeesLabel(),
                    items: ccFeesItems,
                    selected: selected,
                    operator: operator,
                    tone: tone,
                    onApply: (next)=>onPaymentSettingsChange(creditCardFeesSelection({
                            settings,
                            next
                        })),
                    onRemove: ()=>onPaymentSettingsChange(removePaymentField(settings, 'creditCardFeesPaidBy')),
                    biPrefix: "drafts_ai_search_chip_settings_credit_card_fees"
                })
            });
        }
    }
    if (agreementSettings != null) {
        const settings = agreementSettings;
        residualAgreementDescriptors(settings, onAgreementSettingsChange, t).forEach((descriptor)=>units.push({
                field: descriptor.field,
                node: residualChip(descriptor)
            }));
        const terms = settings.termsOfService;
        if (terms != null) {
            var _terms_name;
            const termsName = (_terms_name = terms.name) === null || _terms_name === void 0 ? void 0 : _terms_name.contains;
            if (termsName != null) {
                units.push({
                    field: 'termsOfService',
                    node: /*#__PURE__*/ _jsx(AiFilterChip, {
                        mode: "text",
                        testId: SettingsChipsTestIds.Chip('termsOfService'),
                        fieldLabel: t.settingsTermsOfServiceLabel(),
                        value: termsName,
                        valueLabel: t.settingsTermsOfServiceContains({
                            value: termsName
                        }),
                        tone: tone,
                        onApply: (input)=>onAgreementSettingsChange(termsNameFromInput(settings, input)),
                        onRemove: ()=>onAgreementSettingsChange(removeAgreementField(settings, 'termsOfService')),
                        biPrefix: "drafts_ai_search_chip_settings_terms_of_service",
                        texts: textTexts
                    })
                });
            } else {
                units.push({
                    field: 'termsOfService',
                    node: /*#__PURE__*/ _jsx(FilterChip, {
                        testId: SettingsChipsTestIds.Chip('termsOfService'),
                        fieldLabel: "",
                        valueLabel: t.settingsTermsOfServiceExists(),
                        onRemove: ()=>onAgreementSettingsChange(removeAgreementField(settings, 'termsOfService')),
                        removeBiName: "drafts_ai_search_chip_remove_settings_terms_of_service",
                        tone: tone
                    })
                });
            }
        }
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: units.map((unit, index)=>/*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    index > 0 && /*#__PURE__*/ _jsx(ChipConnector, {
                        testId: SettingsChipsTestIds.AndConnector(index - 1),
                        tone: "and"
                    }),
                    unit.node
                ]
            }, unit.field))
    });
};
